import type { PropsWithChildren } from 'react';
import { Suspense } from 'react';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { v7 } from 'uuid';

import { userCredentialsQueryOptions } from '@/api/endpoints/user_credentials';
import { getGetAllTenantsQueryOptions } from '@/api/gen';
import { AppConfiguration } from '@/components/features/app-configuration/AppConfiguration';
import { RecentsTracker } from '@/components/features/home/RecentsTracker';
import {
  NotificationBannerSlot,
  NotificationModalSlot,
  NotificationPanelSlot,
  NotificationProvider,
} from '@/components/features/notifications/NotificationProvider';
import { DEFAULT_TENANT_ID, TLAI_FEATURE_FLAGS } from '@/constants';
import { FeatureContextProvider } from '@/context/FeatureContext';
import { SettingsContextProvider } from '@/context/SettingsContextProvider';
import { UserSessionContextProvider } from '@/context/UserSessionContext';
import { AppAccess } from '@/features/app-access/AppAccess';
import { AppBar } from '@/features/app-bar/AppBar';
import { AppBarStoreProvider } from '@/features/app-bar/AppBarStoreProvider';
import { useAppBarVisible } from '@/features/app-bar/use-app-bar-visible';
import { AppNavigationSwitch } from '@/features/navigation/AppNavigationSwitch';
import { NavTopBar } from '@/features/navigation/NavTopBar';
import { NavigationStoreProvider, useNavigationStore } from '@/features/navigation/store/NavigationStoreProvider';
import { devToolsQueryOptions } from '@/hooks/use-dev-tool-toggle';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { ErrorBoundary, ErrorFallback } from '@/modus/ErrorBoundary';
import { LoadingSplash } from '@/modus/LoadingSplash';
import { getUserAppFeatures, getUserInfo } from '@/server/user';
import { safeInvoke } from '@/utils/safe-invoke';
import { cn } from '@/utils/styles';

export const Route = createFileRoute('/_protected')({
  staticData: { public: false },
  beforeLoad: async ({ location: requestLocation, context: { queryClient } }) => {
    const sessionId = v7();
    const redirectTo = typeof window === 'undefined' ? requestLocation?.href : window.location.href;
    const { user, tenantId } = await getUserInfo({ data: { redirectTo } });

    const features = await queryClient.ensureQueryData({
      queryKey: ['app', 'features', user.email],
      queryFn: () => getUserAppFeatures(),
    });

    // Prefetch evaluated feature flags into the query cache
    await queryClient.ensureQueryData(devToolsQueryOptions());

    return {
      user,
      sessionId,
      tenantId,
      features,
    };
  },
  pendingComponent: () => <LoadingSplash />,
  loader: async ({ context: { queryClient, tenantId } }) => {
    const [userCredentialsResponse, tenantConfigurationResponse] = await Promise.all([
      safeInvoke(async () => (tenantId ? await queryClient.ensureQueryData(userCredentialsQueryOptions()) : null)),
      safeInvoke(queryClient.ensureQueryData(getGetAllTenantsQueryOptions())),
    ] as const);

    const tenants =
      tenantConfigurationResponse.data?.filter?.((tenant) => tenant.tenant_id !== DEFAULT_TENANT_ID) ?? null;

    return {
      userCredentials: userCredentialsResponse.data?.credentials,
      tenantConfiguration: {
        tenantId,
        tenants,
        tenantsError: tenantConfigurationResponse.error?.message ?? null,
      },
    };
  },
  component: ProtectedLayout,
});

function ProtectedLayout() {
  const { user, sessionId, features } = Route.useRouteContext();

  const {
    userCredentials,
    tenantConfiguration: { tenantId, tenants, tenantsError },
  } = Route.useLoaderData();

  return (
    <UserSessionContextProvider user={user} sessionId={sessionId}>
      <AppConfiguration tenantId={tenantId} tenants={tenants} tenantsError={tenantsError}>
        <FeatureContextProvider features={features}>
          <SettingsContextProvider credentials={userCredentials}>
            <AppBarStoreProvider>
              <NotificationProvider>
                <NavigationStoreProvider>
                  <div className="flex h-dvh w-full min-h-0 flex-col overflow-hidden">
                    <RecentsTracker />
                    <NavTopBar />
                    <div className="flex min-h-0 w-full flex-1 overflow-hidden">
                      <AppNavigationSwitch />
                      <AppBar />
                      <div className="flex min-h-0 min-w-0 max-w-full flex-1 flex-col">
                        <ErrorBoundary fallback={null}>
                          <NotificationBannerSlot />
                        </ErrorBoundary>
                        <ProtectedMain>
                          <AppAccess>
                            <ErrorBoundary fallback={(error) => <ErrorFallback error={error} />}>
                              <Suspense fallback={<LoadingSplash />}>
                                <Outlet />
                              </Suspense>
                            </ErrorBoundary>
                          </AppAccess>
                        </ProtectedMain>
                      </div>
                    </div>
                  </div>
                </NavigationStoreProvider>
                <ErrorBoundary fallback={null}>
                  <NotificationPanelSlot />
                </ErrorBoundary>
                <ErrorBoundary fallback={null}>
                  <NotificationModalSlot />
                </ErrorBoundary>
              </NotificationProvider>
            </AppBarStoreProvider>
          </SettingsContextProvider>
        </FeatureContextProvider>
      </AppConfiguration>
    </UserSessionContextProvider>
  );
}

/**
 * Content container: the legacy shell floats it as a rounded card; the new
 * navigation keeps it flush with rounded outer corners — the visible L2/L3
 * panel carries the left pair, otherwise the content owns all four. Reads the
 * tenant flag, so it must render inside AppConfiguration (unlike
 * ProtectedLayout).
 */
function ProtectedMain({ children }: PropsWithChildren) {
  const newNavigationEnabled = useFeatureFlag(TLAI_FEATURE_FLAGS.NEW_NAVIGATION, false);
  const panelVisible = useNavigationStore((s) => s.panelVisible);
  const appBarVisible = useAppBarVisible();
  const hasLeftPanel = panelVisible || appBarVisible;

  return (
    <main
      className={cn(
        'protected-layout flex min-h-0 min-w-0 max-w-full flex-1 overflow-hidden bg-tessera-foreground-0',
        !newNavigationEnabled && 'm-2 ml-0 rounded-xl',
        newNavigationEnabled && (hasLeftPanel ? 'rounded-r-xl' : 'rounded-xl'),
      )}
    >
      {children}
    </main>
  );
}
